<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { formatTime } from '@/utils/datetime'
import { Button } from '@/components/ui/button'
import { Trash2, MessageSquare } from 'lucide-vue-next'

const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'] || { tz: 'UTC' })

const props = defineProps({
  sessions: {
    type: Array,
    default: () => []
  },
  activeSession: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['select', 'delete'])

const handleSelect = (session) => {
  emit('select', session)
}

const handleDelete = (event, session) => {
  event.stopPropagation()
  emit('delete', session)
}
</script>

<template>
  <div class="flex-1 overflow-y-auto">
    <!-- Empty State -->
    <div v-if="sessions.length === 0" class="p-6 text-center text-muted-foreground text-sm">
      <MessageSquare class="h-8 w-8 mx-auto mb-3 opacity-50" />
      <p>No chat sessions yet</p>
      <p class="text-xs mt-1">Create your first chat to get started</p>
    </div>

    <!-- Sessions List -->
    <div v-else class="divide-y divide-gray-100">
      <div
        v-for="session in sessions"
        :key="session.code"
        @click="handleSelect(session)"
        class="p-3 cursor-pointer hover:bg-gray-50 transition-colors group"
        :class="{
          'bg-blue-50 border-l-2 border-l-blue-600': activeSession?.code === session.code,
          'border-l-2 border-l-transparent': activeSession?.code !== session.code
        }"
      >
        <div class="flex items-start justify-between gap-2">
          <div class="flex-1 min-w-0">
            <h3 class="text-sm font-medium truncate">
              {{ session.title || 'Untitled Chat' }}
            </h3>
            <p class="text-xs text-muted-foreground mt-0.5">
              {{ formatTime(session.created_at, settings.tz, 'MMM D, HH:mm') }}
            </p>
            <p v-if="session.layer_name" class="text-xs text-blue-600 mt-0.5 truncate">
              {{ session.layer_name }}
            </p>
          </div>
          <Button
            @click="(e) => handleDelete(e, session)"
            variant="ghost"
            size="sm"
            class="h-6 w-6 p-0 text-muted-foreground hover:text-red-600 opacity-0 group-hover:opacity-100"
          >
            <Trash2 class="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
